import React, {
  createContext,
  useCallback,
  useContext,
  useEffect
} from 'react';
import { AnalyticsBrowser } from '@segment/analytics-next';
import _get from 'lodash/get';

import { AuthContext } from './AuthContext';
import { useIsInternalUser, useUserMetaData } from 'utils/hooks';

const analytics = new AnalyticsBrowser();
const API_KEY = process.env.REACT_APP_SEGMENT_API_KEY;

/**
 * @type {import('react').Context<{
 *   track: (eventName: string, eventProperties?: Record<string, any>) => void;
 *   page: (pageName: string, pageProperties?: Record<string, any>) => void;
 * }>}
 */
export const SegmentContext = createContext();

export function SegmentProvider({ children = null }) {
  const { userData, isTestRetailer } = useContext(AuthContext);
  const userMetaData = useUserMetaData();
  const isInternalUser = useIsInternalUser();

  const userId = _get(userData, 'userId', '');
  const isTrackable = API_KEY && userId && !isTestRetailer && !isInternalUser;

  useEffect(() => {
    if (!isTrackable) return;

    try {
      analytics.load({ writeKey: API_KEY });
      analytics.identify(userId, userMetaData);
    } catch (error) {
      console.error('Failed to identify user in Segment:', error);
    }

    return () => {
      try {
        analytics.reset();
      } catch (error) {
        console.error('Failed to reset Segment:', error);
      }
    };
  }, [isTrackable, userId, userMetaData]);

  const track = useCallback(
    (eventName = '', eventProperties = {}) => {
      if (!isTrackable || !eventName) return;

      try {
        analytics.track(eventName, { ...userMetaData, ...eventProperties });
      } catch (error) {
        console.error('Failed to track event in Segment:', error);
      }
    },
    [isTrackable, userMetaData]
  );

  const page = useCallback(
    (pageName = '', pageProperties = {}) => {
      if (!isTrackable || !pageName) return;

      try {
        analytics.page(pageName, { ...userMetaData, ...pageProperties });
      } catch (error) {
        console.error('Failed to track page view in Segment:', error);
      }
    },
    [isTrackable, userMetaData]
  );

  return (
    <SegmentContext.Provider value={{ track, page }}>
      {children}
    </SegmentContext.Provider>
  );
}
